import React from 'react';
import { Box, IconButton, Slider } from '@mui/material';
import { SparkButton } from 'sharedComponents/UI';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useGetBlobSasToken } from 'hooks/useGetBlobSasToken';
import { useWorkspaceStore } from 'stores';

type VideoPlayerProps = {
  videoURL: string;
  isExternalVideo: boolean;
  aspectRatio?: string;
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoURL,
  isExternalVideo,
  aspectRatio,
}) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [showControls, setShowControls] = React.useState(true);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [playbackSpeed, setPlaybackSpeed] = React.useState(1);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const [videoFileURL, setVideoFileURL] = React.useState(videoURL);
  const { workspace } = useWorkspaceStore();
  const { refetch: fetchToken } = useGetBlobSasToken(
    workspace?.attributes?.containerName || ''
  );

  React.useEffect(() => {
    fetchToken().then((data) => {
      const token = data.data?.data.attributes.token;
      setVideoFileURL(`${videoURL}?${token}`);
    });
  }, [fetchToken, videoURL]);

  const handlePlayPause = (): void => {
    if (!videoRef.current || isExternalVideo) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVideoClick = (): void => {
    if (!isExternalVideo) {
      handlePlayPause();
    }
  };

  const handleVideoPlay = (): void => {
    setIsPlaying(true);
  };

  const handleVideoPause = (): void => {
    setIsPlaying(false);
  };

  const handleTimeUpdate = (): void => {
    if (videoRef.current && !isDragging) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = (): void => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressChange = (
    event: Event,
    newValue: number | number[]
  ): void => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setCurrentTime(value);
    if (videoRef.current) {
      videoRef.current.currentTime = value;
    }
  };

  const handleProgressChangeStart = (): void => {
    setIsDragging(true);
  };

  const handleProgressChangeEnd = (): void => {
    setIsDragging(false);
  };

  const handleSpeedChange = (): void => {
    if (!videoRef.current) return;

    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];

    setPlaybackSpeed(newSpeed);
    videoRef.current.playbackRate = newSpeed;
  };

  const handleMouseEnter = (): void => {
    if (!isExternalVideo) {
      setShowControls(true);
    }
  };

  const handleMouseLeave = (): void => {
    if (!isExternalVideo && isPlaying) {
      setShowControls(false);
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f0f0f0',
        overflow: 'hidden',
        borderRadius: 2,
        aspectRatio: aspectRatio || '',
        position: 'relative',
        cursor: !isExternalVideo ? 'pointer' : 'default',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isExternalVideo ? (
        <iframe
          width='100%'
          height='100%'
          src={videoURL}
          frameBorder='0'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          allowFullScreen
          title='Video Player'
          style={{
            borderRadius: '8px',
          }}
          referrerPolicy='strict-origin-when-cross-origin'
        />
      ) : (
        <>
          <video
            ref={videoRef}
            width='100%'
            height='100%'
            src={videoFileURL}
            style={{
              borderRadius: '8px',
              objectFit: 'contain',
              // filter: !isPlaying ? 'blur(1px)' : 'none',
              // transition: 'filter 0.3s ease',
            }}
            onClick={handleVideoClick}
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            controls={false}
          />

          {/* Center Play Button (Purple) - Only shown when paused */}
          {!isPlaying && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: (theme) => theme.palette.primary.main,
                borderRadius: '20%',
                width: '90px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2,
                boxShadow: (theme) => theme.shadows[3],
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                  transform: 'translate(-50%, -50%) scale(1.05)',
                },
              }}
              onClick={handlePlayPause}
            >
              <PlayArrowIcon
                sx={{ color: 'white', fontSize: '40px', marginLeft: '4px' }}
              />
            </Box>
          )}

          {/* Bottom Control Bar */}
          {showControls && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '12px',
                left: '12px',
                right: '12px',
                background: 'rgba(0, 0, 0, 0.7)',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                zIndex: 1,
                borderRadius: '8px',
                backdropFilter: 'blur(8px)',
              }}
            >
              {/* Play/Pause Button */}
              <IconButton
                onClick={handlePlayPause}
                sx={{
                  color: 'white',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {isPlaying ? (
                  <PauseIcon sx={{ fontSize: '24px' }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: '24px' }} />
                )}
              </IconButton>

              {/* Current Time */}
              <Box sx={{ color: 'white', fontSize: '14px' }}>
                {formatTime(currentTime)}
              </Box>

              {/* Progress Bar */}
              <Slider
                value={currentTime}
                max={duration}
                onChange={handleProgressChange}
                onMouseDown={handleProgressChangeStart}
                // onChangeCommitted={handleProgressChangeEnd}
                onMouseUp={handleProgressChangeEnd}
                sx={{
                  flex: 1,
                  color: 'white',
                  height: 8,
                  '& .MuiSlider-track': {
                    border: 'none',
                    backgroundColor: (theme) => theme.palette.primary.main,
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '& .MuiSlider-thumb': {
                    height: 16,
                    width: 16,
                    backgroundColor: (theme) => theme.palette.primary.main,
                    border: '2px solid currentColor',
                    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                      boxShadow: 'inherit',
                    },
                    '&:before': {
                      display: 'none',
                    },
                  },
                }}
              />

              {/* Duration */}
              <Box sx={{ color: 'white', fontSize: '14px' }}>
                {formatTime(duration - currentTime)}
              </Box>
              <SparkButton
                onClick={handleSpeedChange}
                variant='contained'
                sx={{
                  borderRadius: '30px',
                  backgroundColor: 'white',
                  color: 'black',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {playbackSpeed}x
              </SparkButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default VideoPlayer;
